import React from 'react';
import { addDays, differenceInDays, format, subDays } from 'date-fns';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { History, Lightbulb, Sparkles, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { fetchWorkspaceAnalytics } from '@/services/workspace';
import { computeWorkspaceInsights } from '@/helpers/analytics-insights.helper';
import { DATE_FORMAT_ISO } from '@/constants/date';
import { cn } from '@/lib/utils';
function buildWindows(from, to, count) {
    const span = Math.max(1, differenceInDays(to, from) + 1);
    const windows = [];
    for (let i = 1; i <= count; i += 1) {
        const winTo = subDays(from, (i - 1) * span + 1);
        const winFrom = addDays(winTo, -(span - 1));
        windows.push({
            key: `${format(winFrom, DATE_FORMAT_ISO)}_${format(winTo, DATE_FORMAT_ISO)}`,
            from: winFrom,
            to: winTo,
            label: `${format(winFrom, 'MMM d')} – ${format(winTo, 'MMM d')}`,
        });
    }
    return windows;
}
export function InsightsHistory({ workspaceId, from, to, excludeDevices, excludeBrowsers, count = 3, className, }) {
    const { t } = useTranslation('glossary');
    const windows = React.useMemo(() => buildWindows(from, to, count), [from, to, count]);
    const queries = useQueries({
        queries: windows.map((w) => ({
            queryKey: [
                'workspace-analytics',
                workspaceId,
                format(w.from, DATE_FORMAT_ISO),
                format(w.to, DATE_FORMAT_ISO),
                excludeDevices,
                excludeBrowsers,
            ],
            queryFn: () => fetchWorkspaceAnalytics({
                workspaceId,
                from: w.from,
                to: w.to,
                excludeDevices,
                excludeBrowsers,
            }),
            staleTime: 5 * 60 * 1000,
        })),
    });
    const entries = windows.map((window, index) => {
        const q = queries[index];
        const analytics = q.data;
        const insights = analytics
            ? computeWorkspaceInsights({
                kpi: analytics.kpi,
                timeline: analytics.timeline ?? [],
                campaigns: analytics.campaignBreakdown ?? [],
                devices: analytics.devices ?? [],
                browsers: analytics.browsers ?? [],
            })
            : [];
        return { window, isLoading: q.isPending, isError: q.isError, insights };
    });
    const hasAnyInsight = entries.some((e) => e.insights.length > 0);
    return (<Card className={cn('border-border/60 bg-card/40 backdrop-blur-sm', className)}>
            <CardHeader className="gap-1 pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <History className="h-4 w-4" aria-hidden/>
                    {t('dashboard.insights.history_title', { defaultValue: 'Insights history' })}
                </CardTitle>
                <CardDescription className="text-xs">
                    {t('dashboard.insights.history_subtitle', {
            defaultValue: 'Same-length windows shifted back — compare what the period looked like before.',
        })}
                </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
                <ol className="relative space-y-5 border-l border-border/60 pl-5">
                    {entries.map(({ window, isLoading, isError, insights }) => (<li key={window.key} className="relative">
                            <span className="absolute -left-[1.4375rem] top-1.5 flex h-3 w-3 items-center justify-center rounded-full border border-border/60 bg-background" aria-hidden>
                                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/60"/>
                            </span>
                            <div className="flex items-baseline justify-between gap-2">
                                <p className="text-sm font-medium tabular-nums">{window.label}</p>
                                {!isLoading && !isError && insights.length === 0 ? (<span className="text-[11px] text-muted-foreground">
                                        {t('dashboard.insights.history_empty', { defaultValue: 'Nothing notable' })}
                                    </span>) : null}
                            </div>
                            <div className="mt-2 space-y-2">
                                {isLoading ? (<div className="h-10 animate-pulse rounded-md bg-muted/30"/>) : isError ? (<p className="text-xs text-muted-foreground">
                                        {t('dashboard.insights.history_error', {
                    defaultValue: 'Could not load this window.',
                })}
                                    </p>) : (insights.map((insight) => <HistoryInsightRow key={insight.key} insight={insight}/>))}
                            </div>
                        </li>))}
                </ol>
                {!queries.some((q) => q.isPending) && !hasAnyInsight ? (<p className="mt-4 rounded-md bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
                        {t('dashboard.insights.history_blank', {
                defaultValue: 'Not enough past data to surface insights yet.',
            })}
                    </p>) : null}
            </CardContent>
        </Card>);
}
function HistoryInsightRow({ insight }) {
    return (<div className={cn('flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs', insight.tone === 'positive' && 'border-emerald-500/30 bg-emerald-500/5', insight.tone === 'negative' && 'border-rose-500/30 bg-rose-500/5', insight.tone === 'neutral' && 'border-border/60 bg-muted/20')}>
            <div className={cn('mt-0.5 shrink-0', insight.tone === 'positive' && 'text-emerald-600 dark:text-emerald-400', insight.tone === 'negative' && 'text-rose-600 dark:text-rose-400', insight.tone === 'neutral' && 'text-muted-foreground')} aria-hidden>
                {insight.tone === 'positive' ? (<TrendingUp className="h-3.5 w-3.5"/>) : insight.tone === 'negative' ? (<TrendingDown className="h-3.5 w-3.5"/>) : (<Lightbulb className="h-3.5 w-3.5"/>)}
            </div>
            <div className="min-w-0 space-y-0.5">
                <p className="font-medium leading-snug text-foreground">{insight.title}</p>
                {insight.body ? <p className="text-[11px] leading-snug text-muted-foreground">{insight.body}</p> : null}
            </div>
            <Sparkles className="ml-auto h-3 w-3 shrink-0 text-muted-foreground/40" aria-hidden/>
        </div>);
}
